/**
 * Auth form autocomplete resolver
 *
 * VAPT requirement (raised for Ujjivan): all authentication form fields must
 * render autocomplete="off". This is an audit/compliance control — it is about
 * the attribute value present in the rendered DOM, NOT runtime browser behavior
 * (modern browsers ignore autocomplete="off" for password managers anyway).
 *
 * The control is enabled per-client via env.js:
 *   window.__env.disablePasswordAutocomplete = true;
 *
 * When the flag is OFF (default, all other clients), fields keep their semantic
 * autocomplete values ('email', 'current-password', 'new-password',
 * 'one-time-code') so password managers and browser autofill continue to work.
 *
 * Usage at each call site:
 *   autoComplete={authAutoComplete('current-password')}
 */

import { envService } from '../services/env.service';

/**
 * Returns the autocomplete value a given auth field should render.
 *
 * @param semanticValue - the field's intended semantic autocomplete token
 *   (e.g. 'email', 'current-password', 'new-password', 'one-time-code')
 * @returns 'off' when the VAPT flag is enabled for this client, otherwise
 *   the passed-in semanticValue unchanged.
 */
export function authAutoComplete(semanticValue: string): string {
  // VAPT wants 'off' on ALL auth fields, so the flag hard-overrides the
  // semantic value when enabled. Default (flag false) keeps semantic values.
  return envService.disablePasswordAutocomplete ? 'off' : semanticValue;
}
